import { useCallback, useContext, useRef } from 'react'
import { QuoteContext } from '../context/QuoteContext'
import { fetchFreshQuote } from './freshQuoteState'

/**
 * Returns a function that loads the server copy of the quote into the editor. Used after a payment
 * the server marked failed: `paymentProcessed` saves the failed payment, then throws, so no quote
 * comes back to the dialog (#24621).
 *
 * Skips the update while the quote is dirty or a save is in flight, so unsaved edits are never
 * dropped. No-op without a `refetchQuote` (customer view, unsaved quotes) or when the fetch fails.
 *
 * The context is read through a ref because the returned function is handed to a dialog when it
 * opens and runs minutes later, after the payment form is submitted. The ref is written during
 * render, not in an effect, so it is never a render behind.
 */
export function useRefreshQuoteFromServer() {
  const context = useContext(QuoteContext)
  const contextRef = useRef(context)
  contextRef.current = context

  return useCallback(async () => {
    const fresh = await fetchFreshQuote(contextRef.current.refetchQuote)
    const { isDirty, isSaving, updateQuote } = contextRef.current
    if (!fresh || isDirty || isSaving) {
      return
    }
    // Push the fresh doc in explicitly: currentQuote is `dirtyQuote || cleanQuote`, so a previous
    // blockDirty update leaves a non-null dirtyQuote that would mask the refreshed clean doc.
    await updateQuote?.({ quote: fresh, autosave: false, blockDirty: true })
  }, [])
}
